// ===============================================================
// 💬 MessagesPage.js — Inbox (UPDATED)
// ✅ FIX: resolve other user photo (PATH/gs:///downloadURL -> http)
// ✅ FIX: official delete behavior (hide chats using visibleTo)
// ✅ FIX: unread badge reads BOTH schemas (unread map OR "unread.uid" flat)
// ✅ FIX: use unified BottomNav so notifications button appears here too
// ✅ UPDATE: visitors removed from MessagesPage
// ✅ UPDATE: customer support -> quick support
// ===============================================================

import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CallsTabContent from "./messages/CallsTabContent";
import { auth, db, storage } from "../firebase";
import { formatDateTimeEn } from "../utils/digits";
import { getDownloadURL, ref as storageRef } from "firebase/storage";

import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  getDoc,
} from "firebase/firestore";

import {
  FaUserFriends,
  FaHeadset,
} from "react-icons/fa";

import "./MessagesPage.css";
import BottomNav from "./BottomNav";
import { defaultAvatarByGender } from "../utils/defaultAvatar";
import { buildExistingChatRoute } from "../navigation/nativeChatRouteBuilder";

// ===============================================================
// 🕒 helper — تحويل أي وقت إلى ms بشكل آمن
// ===============================================================
function toMillisSafe(value) {
  if (!value) return null;

  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value === "number") return value;
  if (value instanceof Date) return value.getTime();

  const t = new Date(value).getTime();
  return Number.isFinite(t) ? t : null;
}

// ===============================================================
// ✅ Image helpers (PATH/gs:///downloadURL -> http)
// ===============================================================
function isHttpUrl(v) {
  return typeof v === "string" && /^https?:\/\//i.test(v);
}

function storagePathFromValue(value) {
  if (!value || typeof value !== "string") return "";

  // already a path
  if (!isHttpUrl(value) && !value.startsWith("gs://")) return value;

  // gs:// keep as is
  if (value.startsWith("gs://")) return value;

  // firebase downloadURL -> extract path
  try {
    const part = value.split("/o/")[1];
    if (!part) return "";
    return decodeURIComponent((part.split("?")[0] || "").trim());
  } catch {
    return "";
  }
}

async function resolveToDownloadUrl(value) {
  if (!value) return "";

  // keep normal http links (non firebase)
  if (isHttpUrl(value) && !value.includes("firebasestorage.googleapis.com")) {
    return value;
  }

  // if it's already a firebase downloadURL, keep it
  if (isHttpUrl(value) && value.includes("firebasestorage.googleapis.com")) {
    return value;
  }

  try {
    const pathOrGs = storagePathFromValue(value) || value;
    return await getDownloadURL(storageRef(storage, pathOrGs));
  } catch (e) {
    console.warn("resolveToDownloadUrl failed:", value, e);
    return "";
  }
}

// ===============================================================
// ✅ unread helper — يدعم حالتين:
// 1) unread: { uid: number }  (Map)
// 2) "unread.uid": number     (Flat key)
// ===============================================================
function getMyUnreadCount(data, myUid) {
  const fromMap =
    data?.unread && typeof data.unread === "object"
      ? data.unread?.[myUid]
      : undefined;

  const fromFlat = data?.[`unread.${myUid}`];

  const n = fromMap != null ? fromMap : fromFlat;
  return Number(n || 0);
}

// ---------------------------------------------------------------
// عنصر محادثة بسيط
// ---------------------------------------------------------------
function ChatItem({ chat, onOpen, t }) {
  const genderFallback = defaultAvatarByGender(chat.otherUserGender);
  const otherName =
    chat.otherUserName || t("chat.user", { defaultValue: "User" });

  const lastText =
    chat.lastMessage ||
    t("chat.start_now", { defaultValue: "Start chatting now" });

  const time = chat.lastAt
    ? formatDateTimeEn(chat.lastAt, {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

  const unread = Number(chat.unreadCount || 0);

  return (
    <button
      type="button"
      className="msg-item"
      onClick={() => onOpen(chat.id, chat.otherUserId)}
    >
      <div className="msg-avatar">
        <img
          src={chat.otherUserPhoto || genderFallback}
          alt={t("chat.avatar_alt", { defaultValue: "Avatar" })}
          onError={(e) => {
            e.target.src = genderFallback;
          }}
        />
      </div>

      <div className="msg-main">
        <div className="msg-row-top">
          <span className="msg-name">{otherName}</span>
          <span className="msg-time">{time}</span>
        </div>

        <div className="msg-row-bottom">
          <span className="msg-last">{lastText}</span>
          {unread > 0 && <span className="msg-unread">{unread}</span>}
        </div>
      </div>
    </button>
  );
}

// ===============================================================
// 🔥 MessagesPage
// ===============================================================
export default function MessagesPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [chats, setChats] = useState([]);
  const [activeTab, setActiveTab] = useState("messages");

  // ✅ Cache لبيانات المستخدم الآخر لتقليل getDoc المتكرر
  const otherUserCacheRef = useRef(new Map());

  useEffect(() => {
    let unsubChats = null;

    const unsubAuth = auth.onAuthStateChanged((user) => {
      if (unsubChats) {
        unsubChats();
        unsubChats = null;
      }

      if (!user?.uid) {
        navigate("/");
        return;
      }

      const myUid = user.uid;

      // Query by membership (users); hide/delete filtered client-side via visibleTo.
      const q = query(
        collection(db, "chats"),
        where("users", "array-contains", myUid),
        orderBy("lastMessageAt", "desc")
      );

      unsubChats = onSnapshot(
        q,
        async (snap) => {
          try {
            const baseList = snap.docs
              .filter((docSnap) => {
                const data = docSnap.data();
                if (
                  Array.isArray(data.visibleTo) &&
                  !data.visibleTo.includes(myUid)
                ) {
                  return false;
                }
                return true;
              })
              .map((docSnap) => {
                const data = docSnap.data();
                const id = docSnap.id;

                const otherUserId =
                  data.users?.find((u) => u !== myUid) || null;

                return {
                  id,
                  otherUserId,
                  otherUserName: t("chat.user", { defaultValue: "مستخدم" }),
                  otherUserPhoto: null,
                  otherUserGender: "",

                  lastMessage: data.lastMessage || "",
                  lastAt: toMillisSafe(data.lastMessageAt),

                  // ✅ FIX: read unread from both schemas
                  unreadCount: getMyUnreadCount(data, myUid),
                };
              })
              .filter(Boolean);

            const enrichedList = await Promise.all(
              baseList.map(async (chat) => {
                if (!chat.otherUserId) return chat;

                const cached = otherUserCacheRef.current.get(chat.otherUserId);
                if (cached) {
                  return {
                    ...chat,
                    otherUserName: cached.otherUserName,
                    otherUserPhoto: cached.otherUserPhoto,
                    otherUserGender: cached.otherUserGender ?? "",
                  };
                }

                try {
                  const userRef = doc(db, "publicProfiles", chat.otherUserId);
                  const userSnap = await getDoc(userRef);

                  if (userSnap.exists()) {
                    const u = userSnap.data();

                    const otherUserName =
                      u.name ||
                      u.displayName ||
                      t("chat.user", { defaultValue: "مستخدم" });
                    const otherUserGender = String(u.gender || "");
                    const rawPhoto = u.profileImage || u.photoURL || null;
                    const otherUserPhoto = rawPhoto
                      ? await resolveToDownloadUrl(rawPhoto)
                      : null;

                    otherUserCacheRef.current.set(chat.otherUserId, {
                      otherUserName,
                      otherUserPhoto,
                      otherUserGender,
                    });

                    return {
                      ...chat,
                      otherUserName,
                      otherUserPhoto,
                      otherUserGender,
                    };
                  }
                } catch (e) {
                  console.error("Load other user error:", e);
                }

                return chat;
              })
            );

            setChats(enrichedList);
            setLoading(false);
          } catch (e) {
            console.error("chats snapshot processing error:", e);
            setLoading(false);
          }
        },
        (err) => {
          console.error("chats snapshot error:", err);
          setLoading(false);
        }
      );
    });

    return () => {
      if (unsubChats) unsubChats();
      unsubAuth();
    };
  }, [navigate, i18n.language]);

  // ✅ تحسين UX: صفّر الإشعار محليًا فور الضغط (قبل فتح الشات)
  const openChat = (chatId, otherUserId) => {
    const chatRoute = buildExistingChatRoute(chatId);
    if (!chatRoute) return;

    setChats((prev) =>
      prev.map((c) => (c.id === chatId ? { ...c, unreadCount: 0 } : c))
    );

    navigate(chatRoute, { state: { otherUserId } });
  };

  return (
    <div className="messages-root">
      <header className="messages-header">
        <div className="msg-header-left">
          <button
            type="button"
            className="msg-icon-btn"
            onClick={() => navigate("/friends")}
          >
            <FaUserFriends size={22} />
          </button>
        </div>

        <div className="msg-tabs">
          <button
            className={activeTab === "messages" ? "msg-tab active" : "msg-tab"}
            onClick={() => setActiveTab("messages")}
          >
            {t("messages.tab_messages", { defaultValue: "Messages" })}
          </button>

          <button
            className={activeTab === "calls" ? "msg-tab active" : "msg-tab"}
            onClick={() => setActiveTab("calls")}
          >
            {t("messages.tab_calls", { defaultValue: "Calls" })}
          </button>
        </div>

        <div className="msg-header-right"></div>
      </header>

      {activeTab === "messages" && (
        <>
          <section className="messages-top-cards">
            <button
              type="button"
              className="msg-top-row"
              onClick={() => navigate("/support/history")}
            >
              <div className="msg-top-text">
                <span className="msg-top-title">
                  {t("support.quick_title", { defaultValue: "الدعم السريع" })}
                </span>
                <span className="msg-top-subtitle">
                  {t("support.quick_sub", {
                    defaultValue: "دعم سريع عبر الدردشة",
                  })}
                </span>
              </div>
              <div className="msg-top-icon support">
                <FaHeadset />
              </div>
            </button>
          </section>

          <section className="messages-list">
            {loading && (
              <p className="messages-empty">
                {t("messages.loading", { defaultValue: "Loading chats..." })}
              </p>
            )}

            {!loading && chats.length === 0 && (
              <p className="messages-empty">
                {t("messages.empty", {
                  defaultValue: "No chats yet. Start connecting with others.",
                })}
              </p>
            )}

            {chats.map((chat) => (
              <ChatItem key={chat.id} chat={chat} onOpen={openChat} t={t} />
            ))}
          </section>
        </>
      )}

      {activeTab === "calls" && (
        <CallsTabContent t={t} navigate={navigate} />
      )}

      <BottomNav active="messages" />
    </div>
  );
}